import React, {useState, useRef, useMemo, useEffect} from 'react'
import {ChevronDown} from 'lucide-react'
import {useNavigate, useLocation} from 'react-router-dom'
import styled from 'styled-components'
import {SIDEBAR_ROUTES} from '~/src/routes/constants'
import {usePermission, useIsPlatformAdmin} from '~/src/hooks/usePermission'
import {isEnabled} from '~/src/featureFlags'

const GlobalButton = styled.div<{active?: boolean}>`
  display: flex;
  align-items: center;
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
  background: transparent;
  color: ${(props) => (props.active ? 'var(--brand-cta)' : 'var(--primary-600)')};

  &:hover {
    background: var(--neutral-50);
    color: var(--brand-cta);
  }
`

const MenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownMenu = styled.div<{$open: boolean}>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: var(--space-2);
  z-index: var(--z-dropdown);
  min-width: 180px;
  background: var(--white);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-strong);
  padding: var(--space-1) 0;
  display: ${({$open}) => ($open ? 'block' : 'none')};
`

const DropdownItem = styled.button<{$active?: boolean}>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: none;
  background: ${(props) => (props.$active ? 'var(--brand-100)' : 'transparent')};
  font-size: var(--fs-body);
  color: ${(props) => (props.$active ? 'var(--brand-cta)' : 'var(--neutral-700)')};
  font-weight: ${(props) => (props.$active ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  cursor: pointer;
  text-align: left;
  min-width: 180px;

  &:hover {
    background: var(--brand-50);
    color: var(--primary-600);
  }
`

const ChevronIcon = styled(ChevronDown)<{$open?: boolean}>`
  width: 16px;
  height: 16px;
  margin-left: var(--space-1);
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
`

const GlobalLabel = styled.span`
  font-weight: var(--fw-semibold);
  font-size: var(--fs-body);
  user-select: none;
  color: inherit;
`

interface GlobalDropdownProps {
  active?: boolean
}

export const GlobalDropdown: React.FC<GlobalDropdownProps> = ({active = false}) => {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const menuRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    setIsOpen((prev) => !prev)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const navigateTo = (path: string) => {
    navigate(path)
    handleClose()
  }

  // Get permissions for global submodules
  // All global settings use 'global' as the module and the specific setting as submodule
  const compensationCodesPermission = usePermission('global', 'compensation_codes')
  const wcCatalogsPermission = usePermission('global', 'wc_catalogs')
  const paycodesPermission = usePermission('global', 'paycodes')
  const authoritiesPermission = usePermission('global', 'authorities')
  const taxPermission = usePermission('global', 'tax')
  const metricsPermission = usePermission('global', 'metrics')
  const rolesPermissionsPermission = usePermission('global', 'roles_permissions')
  const dataMigrationPermission = usePermission('global', 'data_migration')
  // TODO: Replace 'activity_logs' with actual permission key when BE implements it
  const activityLogsPermission = usePermission('global', 'activity_logs')
  const lenderFinancingPermission = usePermission('global', 'lender_financing')
  const financingSchedulesPermission = usePermission('global', 'financing_schedules')
  const timeOffPermission = usePermission('global', 'time_off')
  // Platform admins have full access by default (mirrors the route-guard bypass
  // in AppRoutes); without this, an unseeded lender_financing/financing_schedules
  // node would hide these Global menu items from platform admins.
  const isPlatformAdmin = useIsPlatformAdmin()
  const canViewLenderFinancing = isPlatformAdmin || lenderFinancingPermission.canView
  const canViewFinancingSchedules = isPlatformAdmin || financingSchedulesPermission.canView
  // No platform bypass: global > time_off is seeded on platformadmin, so a
  // Platform-category role that unchecked it must not see the Time Off item.
  const canViewTimeOff = timeOffPermission.canView
  // Filter menu items based on permissions
  const globalMenuItems = useMemo(() => {
    const allItems = [
      {
        label: 'Workers Compensation Codes',
        path: SIDEBAR_ROUTES.GLOBAL_COMPENSATION_CODES,
        canView: compensationCodesPermission.canView,
      },
      {
        label: 'WC Catalogs',
        path: SIDEBAR_ROUTES.GLOBAL_WC_CATALOGS,
        canView: wcCatalogsPermission.canView,
      },
      {
        label: 'Paycodes',
        path: SIDEBAR_ROUTES.GLOBAL_PAYCODES,
        canView: paycodesPermission.canView,
      },
      {
        label: 'Authorities',
        path: SIDEBAR_ROUTES.GLOBAL_AUTHORITIES,
        canView: authoritiesPermission.canView,
      },
      {
        label: 'Tax',
        path: SIDEBAR_ROUTES.GLOBAL_TAX,
        canView: taxPermission.canView,
      },
      // TODO(feature-flag): dashboardMetrics — remove conditional when flag is deleted
      {
        label: 'Metrics',
        path: SIDEBAR_ROUTES.GLOBAL_METRICS,
        canView: metricsPermission.canView && isEnabled('dashboardMetrics'),
      },
      {
        label: 'Roles and Permissions',
        path: '/roles-permissions',
        canView: rolesPermissionsPermission.canView,
      },
      {
        label: 'Data Migration',
        path: SIDEBAR_ROUTES.DATA_MIGRATION,
        canView: dataMigrationPermission.canView,
      },
      {
        label: 'Audit Logs',
        path: SIDEBAR_ROUTES.GLOBAL_ACTIVITY_LOG,
        canView: activityLogsPermission.canView || rolesPermissionsPermission.canView,
      },
      {
        label: 'Lender Financing',
        path: SIDEBAR_ROUTES.GLOBAL_LENDERS,
        canView: canViewLenderFinancing,
      },
      {
        label: 'Financing Schedules',
        path: SIDEBAR_ROUTES.GLOBAL_FINANCING_SCHEDULES,
        canView: canViewFinancingSchedules,
      },
      {
        // Matches the landing page heading; the item's testid is derived from this
        // label (global-dropdown-item-time-off-policies).
        label: 'Time Off Policies',
        path: SIDEBAR_ROUTES.GLOBAL_TIME_OFF,
        // TODO(feature-flag): timeOffPolicies — remove when flag is deleted
        canView: canViewTimeOff && isEnabled('timeOffPolicies'),
      },
    ]
    return allItems.filter((item) => item.canView)
  }, [
    compensationCodesPermission.canView,
    wcCatalogsPermission.canView,
    paycodesPermission.canView,
    authoritiesPermission.canView,
    taxPermission.canView,
    metricsPermission.canView,
    rolesPermissionsPermission.canView,
    dataMigrationPermission.canView,
    activityLogsPermission.canView,
    canViewLenderFinancing,
    canViewFinancingSchedules,
    canViewTimeOff,
  ])

  return (
    <MenuWrapper ref={menuRef}>
      <GlobalButton
        active={active || isOpen}
        onClick={handleClick}
        data-testid="global-dropdown-button">
        <GlobalLabel>Global</GlobalLabel>
        <ChevronIcon $open={isOpen} />
      </GlobalButton>

      <DropdownMenu $open={isOpen} data-testid="global-dropdown-popover">
        {globalMenuItems.map((item) => {
          // Check if this menu item is currently active
          const isItemActive = location.pathname.includes(item.path)

          return (
            <DropdownItem
              key={item.path}
              data-testid={`global-dropdown-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigateTo(item.path)}
              $active={isItemActive}>
              {item.label}
            </DropdownItem>
          )
        })}
      </DropdownMenu>
    </MenuWrapper>
  )
}

export default GlobalDropdown
